import dayjs, { type ConfigType } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import type { Granularity } from "../api-hooks/useKpiBreakdownForChannel";

dayjs.extend(isoWeek);

type GranularityThresholds = {
  /**
   * Number of days (exclusive) after which weekly granularity should be used.
   * Defaults to 90 days (roughly three months).
   */
  weekly?: number;
  /**
   * Number of days (exclusive) after which monthly granularity should be used.
   * Defaults to 365 days (one year).
   */
  monthly?: number;
};

const DEFAULT_THRESHOLDS: Required<GranularityThresholds> = {
  weekly: 60,
  monthly: 180,
};

/**
 * Determines the granularity for KPI requests based on a date range.
 *
 * @param {ConfigType | null | undefined} startDate Start of the date range.
 * @param {ConfigType | null | undefined} endDate End of the date range.
 * @param {GranularityThresholds} thresholds Optional overrides for the weekly/monthly thresholds.
 * @return {Granularity} The granularity that should be used (daily, weekly, or monthly).
 */
export function getGranularityForRange(
  startDate: ConfigType | null | undefined,
  endDate: ConfigType | null | undefined,
  thresholds: GranularityThresholds = {}
): Granularity {
  const { weekly, monthly } = { ...DEFAULT_THRESHOLDS, ...thresholds };

  if (!startDate || !endDate) {
    return "daily";
  }

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (!start.isValid() || !end.isValid()) {
    return "daily";
  }

  const daysDifference = Math.abs(end.diff(start, "day"));

  if (daysDifference > monthly) {
    return "monthly";
  }

  if (daysDifference > weekly) {
    return "weekly";
  }

  return "daily";
}

// Granularity hierarchy from finest to coarsest
const GRANULARITY_ORDER: Granularity[] = ["daily", "weekly", "monthly"];

function spansMultipleWeeks(
  startDate: ConfigType | null | undefined,
  endDate: ConfigType | null | undefined
): boolean {
  if (!startDate || !endDate) return false;
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return false;

  // Compare the start of the ISO week for both dates
  return !start.startOf("isoWeek").isSame(end.startOf("isoWeek"), "day");
}

function spansMultipleMonths(
  startDate: ConfigType | null | undefined,
  endDate: ConfigType | null | undefined
): boolean {
  if (!startDate || !endDate) return false;
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return false;

  // Compare the start of the month for both dates
  return !start.startOf("month").isSame(end.startOf("month"), "day");
}

export interface GranularityOption {
  value: Granularity;
  label: string;
  enabled: boolean;
  disabledReason?: string;
}

export function getGranularityOptionsWithStatus(
  autoGranularity: Granularity,
  startDate?: ConfigType | null,
  endDate?: ConfigType | null
): GranularityOption[] {
  const autoIndex = GRANULARITY_ORDER.indexOf(autoGranularity);

  return GRANULARITY_ORDER.map((granularity, index) => {
    const label = granularity.charAt(0).toUpperCase() + granularity.slice(1);

    // Check if it's finer than auto (upstream - not allowed)
    if (index < autoIndex) {
      return {
        value: granularity,
        label,
        enabled: false,
        disabledReason:
          granularity === "daily"
            ? "Not available for ranges over 60 days"
            : "Not available for ranges over 180 days",
      };
    }

    // Check span requirements
    if (granularity === "weekly" && !spansMultipleWeeks(startDate, endDate)) {
      return {
        value: granularity,
        label,
        enabled: false,
        disabledReason: "Range must span at least 2 weeks",
      };
    }

    if (granularity === "monthly" && !spansMultipleMonths(startDate, endDate)) {
      return {
        value: granularity,
        label,
        enabled: false,
        disabledReason: "Range must span at least 2 months",
      };
    }

    return {
      value: granularity,
      label,
      enabled: true,
    };
  });
}

export function getAvailableGranularityOptions(
  autoGranularity: Granularity,
  startDate?: ConfigType | null,
  endDate?: ConfigType | null
): Granularity[] {
  return getGranularityOptionsWithStatus(autoGranularity, startDate, endDate)
    .filter((opt) => opt.enabled)
    .map((opt) => opt.value);
}

export type GranularityPreference = Granularity;

export function getEffectiveGranularity(
  userSelection: GranularityPreference | null | undefined,
  autoGranularity: Granularity,
  startDate?: ConfigType | null,
  endDate?: ConfigType | null
): Granularity {
  if (!userSelection) {
    return autoGranularity;
  }

  const availableOptions = getAvailableGranularityOptions(
    autoGranularity,
    startDate,
    endDate
  );
  if (availableOptions.includes(userSelection)) {
    return userSelection;
  }

  // Selection is invalid, fall back to auto-calculated granularity
  return autoGranularity;
}

export type { GranularityThresholds };
